import { useRef, type ReactNode } from 'react';
import type { Horizon } from '../../content/site';
import { gsap, useGSAP, EASE, MOTION_OK, DESKTOP } from '../../lib/gsap';
import { HALO } from './halo';
import { glue } from './glue';
import { reachTime } from './reach';

/**
 * Desktop placement along the horizon line (12-column grid). The gaps widen with
 * time (today is close, the long term is far) and each horizon hangs a little lower
 * from the line on its own stem. Tablet keeps an even 3 x 4 split.
 */
const PLACEMENT = [
	{ col: 'md:col-span-4 lg:col-span-3 lg:col-start-1', stem: 'md:h-8', pad: 'md:pt-12' },
	{ col: 'md:col-span-4 lg:col-span-3 lg:col-start-5', stem: 'md:h-[5.5rem]', pad: 'md:pt-[6.5rem]' },
	{ col: 'md:col-span-4 lg:col-span-3 lg:col-start-10', stem: 'md:h-[9rem]', pad: 'md:pt-[10rem]' },
];

const DRAW = 1.6;

/**
 * The three horizons as one progression: a hairline that draws itself (left to right
 * from 768px, top to bottom on mobile) and horizons that appear as the line reaches them.
 */
export function Horizons({ items, firstExtra }: { items: Horizon[]; firstExtra?: ReactNode }) {
	const ref = useRef<HTMLDivElement>(null);

	useGSAP(
		() => {
			const wrap = ref.current;
			if (!wrap) return;
			const mm = gsap.matchMedia();

			mm.add(
				{
					wide: `${MOTION_OK} and ${DESKTOP}`,
					narrow: `${MOTION_OK} and (max-width: 767.98px)`,
				},
				(context) => {
					const wide = Boolean(context.conditions?.wide);
					const line = wrap.querySelector<HTMLElement>(wide ? '[data-line="x"]' : '[data-line="y"]');
					if (!line) return;

					// Measure before any tween renders (from() hides the line immediately).
					const box = line.getBoundingClientRect();
					const horizons = gsap.utils.toArray<HTMLElement>('[data-horizon]', wrap).map((el) => {
						const r = el.getBoundingClientRect();
						const fraction = wide ? (r.left - box.left) / box.width : (r.top - box.top) / box.height;
						return { el, at: reachTime(fraction) * DRAW };
					});

					const tl = gsap.timeline({ scrollTrigger: { trigger: wrap, start: 'top 78%' } });
					tl.from(line, { [wide ? 'scaleX' : 'scaleY']: 0, duration: DRAW, ease: 'power2.inOut' }, 0);

					horizons.forEach(({ el, at }) => {
						const tick = el.querySelector('[data-tick]');
						const stem = el.querySelector('[data-stem]');
						const parts = el.querySelectorAll('[data-part]');
						if (tick) tl.from(tick, { scale: 0, duration: 0.6, ease: EASE }, at);
						if (wide && stem) tl.from(stem, { scaleY: 0, duration: 0.8, ease: 'power2.out' }, at + 0.05);
						tl.from(parts, { y: 18, opacity: 0, duration: 1, ease: EASE, stagger: 0.07 }, at + (wide ? 0.3 : 0.08));
					});
				},
			);
		},
		{ scope: ref },
	);

	return (
		<div ref={ref} className="relative">
			{/* The horizon line: horizontal from 768px, vertical on mobile. */}
			<span aria-hidden data-line="x" className="absolute inset-x-0 top-0 hidden h-px origin-left bg-mineral/15 md:block" />
			<span aria-hidden data-line="y" className="absolute bottom-0 left-0 top-0 w-px origin-top bg-mineral/15 md:hidden" />

			<ol className="grid gap-y-14 pl-8 md:grid-cols-12 md:gap-x-6 md:pl-0">
				{items.map((horizon, i) => {
					const place = PLACEMENT[i % PLACEMENT.length];
					return (
						<li key={horizon.label} data-horizon className={`relative ${place.col} ${place.pad}`}>
							<span
								aria-hidden
								data-stem
								className={`absolute left-0 top-0 hidden w-px origin-top bg-mineral/15 md:block ${place.stem}`}
							/>
							<span
								aria-hidden
								data-tick
								className="absolute -left-8 top-[5.5px] -ml-[7px] h-0.5 w-3.5 bg-solar md:left-0 md:top-0 md:-ml-px md:-mt-[7px] md:h-3.5 md:w-0.5"
							/>
							<p data-part className="font-mono text-[13px] font-medium leading-none text-solar">
								{horizon.label}
							</p>
							<h3
								data-part
								className="mt-4 font-display text-[clamp(1.5rem,2.1vw,2rem)] font-bold leading-[1.05] tracking-[-0.02em] text-mineral"
							>
								{glue(horizon.title)}
							</h3>
							<p data-part className={`mt-3 max-w-[34ch] text-base leading-relaxed text-dusk-muted ${HALO}`}>
								{glue(horizon.text)}
							</p>
							{i === 0 && firstExtra}
						</li>
					);
				})}
			</ol>
		</div>
	);
}
